import React from 'react';
import { Footerdemo } from './ui/footer-section';
import { SEOHead } from '../lib/seo';
import { Badge } from "@/components/ui/badge";
import { BrainCircuit, Zap, CheckCircle, X, Target, Shield, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

export default function WhyThisContentTool() {
  return (
    <div className="min-h-screen flex flex-col">
      <SEOHead
        title="Why We Built a Better AI Content Tool"
        description="Discover why AISEOBlogGenerator was created to solve the real problems marketers face with AI content. Learn about our marketing-first approach, premium AI models, and strike list technology that eliminates corporate buzzwords."
        type="article"
        canonicalUrl="https://aiseobloggenerator.com/why"
        author={{
          name: "Gabe Fletcher",
          url: "https://bolters.io"
        }}
      />
      <div className="flex-1 pt-16">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-b from-cream to-blue-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <Badge variant="outline" className="mb-3 bg-dogblue text-white hover:bg-dogblue/90 px-3 py-1">Meet Rex the CopyBiter</Badge>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                Why We Built a <span className="text-dogbrown line-through">Different</span> <span className="text-dogblue">Better</span> Content Tool
              </h1>
              <p className="text-lg md:text-xl text-gray-700 mb-12">
                We created AISEOBlogGenerator because we were tired of "dog water" AI content that sounds robotic, cliché, and just plain bad. No cap, fr fr.
              </p>
              
              <div className="flex justify-center mb-8">
                <motion.div
                  initial={{ rotate: -5 }}
                  animate={{ rotate: 5 }}
                  transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
                >
                  <img 
                    src="https://emp3.b-cdn.net/seoblogtool/png/noneofthis.png" 
                    alt="Rex the CopyBiter" 
                    className="w-64 h-auto"
                  />
                </motion.div>
              </div>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mt-12">
              {/* Card 1: Built By A Marketer */}
              <div className="p-6">
                {/* Image with hover animation - no containing card or background */}
                <motion.img 
                  src="https://emp3.b-cdn.net/seoblogtool/png/dogwithpencil.png" 
                  alt="Dog with pencil"
                  className="w-24 h-auto mx-auto mb-6"
                  whileHover={{ 
                    rotate: 10, 
                    scale: 1.1, 
                    transition: { 
                      type: "spring", 
                      stiffness: 300, 
                      damping: 10 
                    } 
                  }}
                />
                <div className="w-16 h-1.5 rounded-full bg-dogbrown mb-4"></div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">Built By A Marketer</h3>
                <p className="text-gray-700">
                  Created by someone who understands conversion, customer journey, and what makes content actually go brrrr. Not these yee-yee coding bros making AI tools.
                </p>
              </div>
              
              {/* Card 2: No Prompt Engineering */}
              <div className="p-6">
                {/* Image with hover animation - no containing card or background */}
                <motion.img 
                  src="https://emp3.b-cdn.net/seoblogtool/png/letaidothewriting.png" 
                  alt="Let AI do the writing"
                  className="w-24 h-auto mx-auto mb-6"
                  whileHover={{ 
                    rotate: -10, 
                    scale: 1.1, 
                    transition: { 
                      type: "spring", 
                      stiffness: 300, 
                      damping: 10 
                    } 
                  }}
                />
                <div className="w-16 h-1.5 rounded-full bg-dogblue mb-4"></div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">No Prompt Engineering</h3>
                <p className="text-gray-700">
                  Stop wasting time crafting the "perfect prompt" fam. We've done the hard work so you can just yeet content into existence with one click.
                </p>
              </div>
              
              {/* Card 3: Only Good LLMs */}
              <div className="p-6">
                {/* Image with hover animation - no containing card or background */}
                <motion.img 
                  src="https://emp3.b-cdn.net/seoblogtool/png/dogwithmoneytag.png" 
                  alt="Dog with money tag"
                  className="w-24 h-auto mx-auto mb-6"
                  whileHover={{ 
                    rotate: 10, 
                    scale: 1.1, 
                    transition: { 
                      type: "spring", 
                      stiffness: 300, 
                      damping: 10 
                    } 
                  }}
                />
                <div className="w-16 h-1.5 rounded-full bg-green-500 mb-4"></div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">Only Good LLMs</h3>
                <p className="text-gray-700">
                  Generate 4000-word absolute CHAD content with o1, o3, and Claude 3.7. Built different. Absolutely bussin. No more BS GPT-3.5 Turbo.
                </p>
              </div>
            </div>
          </div>
        </section>
        
        {/* What Makes Us Different Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-12 text-center">What Makes Us Different?</h2>
              
              <div className="space-y-16">
                {/* No Corporate Buzzwords */}
                <div className="flex flex-col md:flex-row gap-8 items-center">
                  <div className="md:w-1/2">
                    <h3 className="text-2xl font-bold mb-4 text-dogbrown">No Corporate Buzzwords</h3>
                    <p className="text-gray-700 mb-6">
                      We've all read AI content that's full of phrases like "In today's fast-paced digital landscape" and "leveraging synergies to drive results." Our tool says "nah fam, we ain't about that life." No cap.
                    </p>
                    <div className="space-y-3">
                      <div className="flex items-start gap-2">
                        <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <p className="text-gray-700 italic">"In today's ever-evolving digital landscape..."</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <p className="text-gray-700 italic">"Leveraging the power of innovative solutions..."</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <p className="text-gray-700">"Most companies waste money on SEO because they're focused on the wrong metrics."</p>
                      </div>
                    </div>
                  </div>
                  <div className="md:w-1/2 flex justify-center">
                    <motion.img 
                      src="https://emp3.b-cdn.net/seoblogtool/png/nobadphrases.png" 
                      alt="Ban the awful phrases" 
                      className="w-auto h-auto max-w-full"
                      whileHover={{ 
                        scale: 1.1, 
                        rotate: 10,
                        transition: { 
                          type: "spring", 
                          stiffness: 300, 
                          damping: 10 
                        } 
                      }}
                    />
                  </div>
                </div>
                
                {/* Marketing-First Approach */}
                <div className="flex flex-col md:flex-row gap-8 items-center">
                  <div className="md:w-1/2 md:order-2">
                    <h3 className="text-2xl font-bold mb-4 text-dogbrown">Marketing-First Approach</h3>
                    <p className="text-gray-700 mb-6">
                      Most AI writing tools are built by engineers living in Silicon Valley who think drinking soylent is a personality trait. Ours is built by a real marketer who actually touches grass and understands that content needs to drive business results, not just fill space.
                    </p>
                    <div className="space-y-3">
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <p className="text-gray-700">Target audience selection for precise messaging</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <p className="text-gray-700">Content goals aligned with your marketing funnel</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <p className="text-gray-700">Strategic CTAs based on buyer journey stage</p>
                      </div>
                    </div>
                  </div>
                  <div className="md:w-1/2 md:order-1 flex justify-center">
                    <motion.img 
                      src="https://emp3.b-cdn.net/seoblogtool/png/dogwithmoneytag.png" 
                      alt="Rex with money tag" 
                      className="w-auto h-auto max-w-full"
                      whileHover={{ 
                        scale: 1.1, 
                        rotate: -10,
                        transition: { 
                          type: "spring", 
                          stiffness: 300, 
                          damping: 10 
                        } 
                      }}
                    />
                  </div>
                </div>
                
                {/* Premium AI Models */}
                <div className="flex flex-col md:flex-row gap-8 items-center">
                  <div className="md:w-1/2">
                    <h3 className="text-2xl font-bold mb-4 text-dogbrown">Premium AI Models</h3>
                    <p className="text-gray-700 mb-6">
                      We don't use basic models that produce mid content. Our tool leverages the most goated AI available rn, including models that cost us 10-20x more than standard options. We're literally throwing money away here.
                    </p>
                    <div className="space-y-3">
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <div className="flex items-center">
                          <Shield className="w-5 h-5 text-dogblue mr-2" />
                          <span className="font-semibold">Claude 3.7 Sonnet</span>
                        </div>
                        <p className="text-sm text-gray-700 mt-1">
                          Industry-leading model with advanced reasoning and language capabilities. Absolute smoke.
                        </p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <div className="flex items-center">
                          <Zap className="w-5 h-5 text-dogblue mr-2" />
                          <span className="font-semibold">OpenAI o1</span>
                        </div>
                        <p className="text-sm text-gray-700 mt-1">
                          Business-grade model with exceptional performance for professional content. Hits different fr fr.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="md:w-1/2 flex justify-center">
                    <motion.img 
                      src="https://emp3.b-cdn.net/seoblogtool/png/enterprise.png" 
                      alt="Rex enterprise pricing" 
                      className="w-auto h-auto max-w-full"
                      whileHover={{ 
                        scale: 1.1, 
                        rotate: 10,
                        transition: { 
                          type: "spring", 
                          stiffness: 300, 
                          damping: 10 
                        } 
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Secret Sauce Section */}
        <section className="py-20 bg-gradient-to-b from-blue-50 to-cream">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-8 text-center">Our Secret Sauce: Rex The CopyBiter</h2>
              <div className="flex flex-col md:flex-row items-center gap-8 mb-12">
                <div className="md:w-1/3">
                  <motion.div
                    initial={{ y: 10 }}
                    animate={{ y: -10 }}
                    transition={{ duration: 2, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
                  >
                    <img 
                      src="https://emp3.b-cdn.net/seoblogtool/png/seonotdogshit.png" 
                      alt="SEO AI Not Dog Sh*t" 
                      className="rounded-full border-4 border-white shadow-lg"
                    />
                  </motion.div>
                </div>
                <div className="md:w-2/3">
                  <h3 className="text-2xl font-bold mb-4 text-dogbrown">Meet Rex, Your Content Quality Guard</h3>
                  <p className="text-gray-700 mb-4">
                    Rex is our mascot and the guardian of good content. He's a CopyBiter - a rare breed that can smell bad writing from a mile away and is allergic to corporate jargon.
                  </p>
                  <p className="text-gray-700">
                    His favorite hobbies include chewing up bloated buzzwords, burying clichés in the backyard, and leaving "presents" on overused AI phrases. When he's not patrolling your content, he's sleeping in the sun dreaming of conversion rates.
                  </p>
                </div>
              </div>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-lg shadow-md text-center">
                  <img 
                    src="https://emp3.b-cdn.net/seoblogtool/png/dontmakethatkindacontent.png" 
                    alt="We don't make cringe copy" 
                    className="w-40 h-auto mx-auto mb-4"
                  />
                  <h3 className="font-bold text-lg mb-2">Cringe-Free Zone</h3>
                  <p className="text-gray-700">
                    Rex barks at anything that makes your target audience roll their eyes.
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-md text-center">
                  <img 
                    src="https://emp3.b-cdn.net/seoblogtool/png/wakeup.png" 
                    alt="Sound like an actual human" 
                    className="w-40 h-auto mx-auto mb-4"
                  />
                  <h3 className="font-bold text-lg mb-2">Human-Like Writing</h3>
                  <p className="text-gray-700">
                    Our AI writes like someone who actually has a personality. Wild concept, we know.
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-md text-center">
                  <img 
                    src="https://emp3.b-cdn.net/seoblogtool/png/notwithnoncringe.png" 
                    alt="Made with non-cringe" 
                    className="w-40 h-auto mx-auto mb-4"
                  />
                  <h3 className="font-bold text-lg mb-2">Non-Cringe Guarantee</h3>
                  <p className="text-gray-700">
                    If you find corporate buzzwords in our output, Rex will personally apologize.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Social Proof Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-12">What People Are Saying</h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-cream rounded-lg p-6 border border-gray-200 shadow-md relative">
                  <div className="absolute -top-6 -right-6">
                    <img 
                      src="https://emp3.b-cdn.net/seoblogtool/png/broyourcontentsucks.png" 
                      alt="Bro your content sucks" 
                      className="w-20 h-auto" 
                    />
                  </div>
                  <p className="italic text-gray-700 mb-4">
                    "I've spent thousands on other AI writing tools and the content always needed hours of editing. This tool nails it first try. Damn."
                  </p>
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-dogblue flex items-center justify-center text-white font-bold mr-3">JP</div>
                    <div className="text-left">
                      <p className="font-bold">James P.</p>
                      <p className="text-sm text-gray-600">Marketing Director</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-cream rounded-lg p-6 border border-gray-200 shadow-md relative">
                  <div className="absolute -top-6 -right-6">
                    <img 
                      src="https://emp3.b-cdn.net/seoblogtool/png/dogwithpencil.png" 
                      alt="Dog with pencil" 
                      className="w-20 h-auto" 
                    />
                  </div>
                  <p className="italic text-gray-700 mb-4">
                    "I'm blown away by how this actually understands my audience. It's like having an extra marketing team member who never sleeps."
                  </p>
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-dogbrown flex items-center justify-center text-white font-bold mr-3">SV</div>
                    <div className="text-left">
                      <p className="font-bold">Sarah V.</p>
                      <p className="text-sm text-gray-600">Content Strategist</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Creator Section */}
        <section className="py-20 bg-cream">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">From the Creator</h2>
              <div className="flex flex-col items-center">
                <div className="w-24 h-24 rounded-full overflow-hidden mb-4">
                  <img 
                    src="https://modest-heron-656.convex.cloud/api/storage/kg2fw2msfvtgmzpefd9755rskn7cdapw" 
                    alt="Gabe Fletcher" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xl font-bold">Gabe Fletcher</h3>
                <p className="text-gray-700 mb-6">Founder of AISEOBlogGenerator.com & EditMyPodcast.agency</p>
                
                <blockquote className="bg-white p-6 rounded-lg text-left border-l-4 border-dogblue shadow-md mb-8 text-gray-700">
                  <div className="absolute -right-4 -top-8">
                    <img 
                      src="https://emp3.b-cdn.net/seoblogtool/png/aicontentwriter2.png" 
                      alt="AI Content Writer" 
                      className="w-24 h-auto" 
                    />
                  </div>
                  <p className="italic mb-4 relative z-10">
                    "I wanted a very specific copy tool, so I made one. The world will be shaped by those that do for themselves first and then help others. Developing is no different. I was tired of generic AI content that sounds like it was written by a robot – so I built a solution that creates content I'd actually want to publish."
                  </p>
                  <p className="italic">
                    "I'm out here fighting ChatGPT so you don't have to, fam."
                  </p>
                </blockquote>
                
                <p className="text-gray-700 max-w-2xl">
                  With 20 years of experience in marketing, web development, and SEO, Gabe created AISEOBlogGenerator to solve the real-world problems marketers face when trying to create content at scale without sacrificing quality. No cap, just vibes.
                </p>
                
                <div className="mt-12">
                  <Link to="/writer">
                    <Button className="bg-dogbrown text-white hover:bg-dogbrown/90">
                      Try It Yourself Now
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footerdemo />
    </div>
  );
}